/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import Button from '@react/react-spectrum/Button';
import Close from '@react/react-spectrum/Icon/Close';
import React, {useEffect, useState} from 'react';
import {AppHomeData} from '../../containers/AppHome/types';
import Icon from '../Icon/Icon';
import NewFileDropCard from '../NewFileDropCard/NewFileDropCard';
import getConfig from '../../../config';
import open from '../../../utils/open';
import './AppHomeTab.scss';
import {AppHomeCard} from './AppHomeCard';
import {AppHomeCardIcon} from './AppHomeCardIcon';
import NewCanvasCard from '../NewCanvasCard/NewCanvasCard';

type Props = {
	recent?: any;
	appHomeData: AppHomeData;
	leftRailOpen: boolean;
	intl: any;
	createSpacesCallback: Function;
};

const AppHomeTab = ({recent, appHomeData, leftRailOpen, intl, createSpacesCallback}: Props) => {
	const {tabs} = appHomeData.startRow;

	const [selectedTab, setSelectedTab] = useState(tabs[0].name);
	const [isOpen, setIsOpen] = useState(true);
	const cardLoadDelay = 4000;

	const handleTabClick = newtab => (e) => {
		e.preventDefault();
		setSelectedTab(newtab);
	};

	const extraData = appHomeData.data;

	const topContainerStyle: React.CSSProperties = {
		backgroundColor: appHomeData.data.backgroundColor,
		backgroundImage: extraData.bannerImgUrl
			? `url(https://${window.location.host}/assets/${extraData.bannerImgUrl})`
			: 'none',
		// backgroundPosition: !!extraData.backgroundPosition ? extraData.backgroundPosition : 'center',
	};

	const closeBanner = () => {
		setIsOpen(false);
	};

	const canvasSpinner = !!window.cchome.util.hasFeature('canvas_spinner');
	const comingSoonDisabled = !!window.cchome.util.hasFeature('apphomes_disable_soon');

	const onClickTakeTour = () => {
		const photoshopWebUrl = getConfig('PHOTOSHOP_WEB');
		const tourUrl = `${photoshopWebUrl}?learnid=5AD1CFE88C3E`;
		open(tourUrl, '_self');
	};

	useEffect(() => {
		// if (appHomeData.name === 'Fred' && !document.getElementById('fred-preload')) {
		// 	preloadLink.id = 'fred-preload';
		// 	preloadLink.href = 'https://stage.fred.corp.adobe.com/new';
		// 	document.head.appendChild(preloadLink);
		// } else
		if (window.cchome.util.hasFeature('psw_preload') && appHomeData.sapCode === 'PHSP' && !document.getElementById('ps-preload')) {
			// Triggers the preload of PS Web via an invisible iframe
			const preloadIframe = document.createElement('iframe');
			preloadIframe.id = 'ps-preload';
			preloadIframe.style.display = 'none';
			const photoshopWebUrl = getConfig('PHOTOSHOP_WEB');
			preloadIframe.src = `${photoshopWebUrl}preload.html`;
			document.body.appendChild(preloadIframe);
			window.cchome.util.logger.log('PS Web App Home Tab', 'Preloaded PS Web iframe');
		}
	}, []);

	const renderCard = (link, i) => {
		switch (link.type) {
			case 'dropbox':
				return (
					<NewFileDropCard
						key={`${link.type}-${i}`}
						createNewFileLink={link.link}
						iconImgSrc={link.imgUrl}
						cardTitle={link.text}
						i={i}
						intl={intl}
						appContextType={appHomeData.sapCode}
					/>
				);
			case 'icon':
				return (
					<AppHomeCardIcon
						key={`${link.type}-${i}`}
						text={link.text}
						link={link.link}
						imgUrl={link.imgUrl}
						type={link.type}
						title={intl.formatMessage({id: 'app.apphomes.canvas.template'})}
						loadDelay={cardLoadDelay}
						spinner={canvasSpinner}
						apphomeData={appHomeData}
						intl={intl}
					/>
				);
			case 'dialog':
				// Fred canvas create card
				return <NewCanvasCard intl={intl} createSpacesCallback={createSpacesCallback} />;
			case 'card':
				return (
					<AppHomeCard
						key={`${link.type}-${i}`}
						text={link.text}
						link={link.link}
						imgUrl={link.imgUrl}
						type={link.type}
						title={intl.formatMessage({id: 'app.apphomes.canvas.template'})}
						loadDelay={cardLoadDelay}
						comingSoon={!comingSoonDisabled}
						spinner={canvasSpinner}
						apphomeData={appHomeData}
						intl={intl}
					/>
				);
			case 'tutorial':
				return (
					<AppHomeCard
						key={`${link.type}-${i}`}
						text={link.text}
						link={link.link}
						imgUrl={link.imgUrl}
						type={link.type}
						title={intl.formatMessage({id: 'appHomes.cards.tutorial.hands'})}
						loadDelay={cardLoadDelay}
						comingSoon={!comingSoonDisabled}
						spinner={canvasSpinner}
						apphomeData={appHomeData}
						intl={intl}
					/>
				);
			default:
				return <></>;
		}
	};

	const takeTourButton
		= window.cchome.util.hasFeature('tour_button') && appHomeData.sapCode === 'PHSP' ? (
			<Button
				label='Take the tour'
				variant={appHomeData.data.btnVariant ? appHomeData.data.btnVariant : 'primary'}
				onClick={onClickTakeTour}
			/>
		) : null;

	return (
		<div className={`app-home-tab${leftRailOpen ? ' leftnav-open' : ''}`}>
			{isOpen && (
				<div className='top-container' style={topContainerStyle}>
					<div className='top-inner left'>
						<h1
							style={{
								color: appHomeData.data.textColor ? appHomeData.data.textColor : 'inherit',
							}}
						>
							Welcome to {appHomeData.name}
						</h1>
						<p
							style={{
								color: appHomeData.data.textColor ? appHomeData.data.textColor : 'inherit',
							}}
						>
							{appHomeData.subtitle}
						</p>
						{takeTourButton}
					</div>
					<div className='top-inner right'>
						<Button quiet variant='action' icon={<Close />} onClick={closeBanner} />
					</div>
				</div>
			)}
			<div className='bottom-container'>
				<div className='start-action-container'>
					{tabs.length > 0
						&& (tabs.length > 1 ? (
							<>
								<ul className='tab-list'>
									{tabs.map((tab, i) =>
										(tab.type === 'tab' ? (
											<li
												key={`${tab.name}-tab-${i}`}
												className={`tab-selector${selectedTab === tab.name ? ' selected' : ''}`}
												onClick={handleTabClick(tab.name)}
											>
												<Icon name={tab.icon} />
												{tab.name}
											</li>
										) : (
											<button
												key={`${tab.name}-tab-${i}`}
												onClick={(e) => {
													e.preventDefault();
												}}
											>
												<Icon name={tab.icon} />
												{tab.name}
											</button>
										)),
									)}
								</ul>
								<div className='tab-body'>
									{tabs.map((tab, i) => (
										<div
											key={`${tab.name}-body-${i}`}
											className={`action-row${selectedTab === tab.name ? '' : ' hidden'}`}
										>
											{tab.links?.map(renderCard)}
										</div>
									))}
								</div>
							</>
						) : (
							<>
								<div className='tab-body'>
									<div
										key={`${tabs[0].name}-body`}
										className={`action-row${selectedTab === tabs[0].name ? '' : ' hidden'}`}
									>
										{tabs[0].links?.map(renderCard)}
									</div>
								</div>
							</>
						))}
				</div>
				<div className='recent-container'>{recent}</div>
			</div>
		</div>
	);
};

export default AppHomeTab;
